import { serializeDebugSnapshot } from "../debug/StateLogger";
import type { DebugSaveState, DebugSnapshot } from "../types/debug";

export interface DebugOverlayDomFactory {
  createElement(tagName: string): HTMLElement;
}

const EMPTY_SAVE_STATE: DebugSaveState = {
  clearedLevels: [],
  perfectedLevels: [],
  unlockedLevels: [],
  unlockedChapters: [],
  passedLocks: [],
  collectiblesByLevel: {},
  permanentRevealLevels: [],
};

function defaultDomFactory(): DebugOverlayDomFactory {
  return {
    createElement: (tagName: string) => document.createElement(tagName),
  };
}

export class DebugOverlay {
  private readonly elements = new Map<string, HTMLElement>();

  public render(
    container: HTMLElement,
    snapshot: DebugSnapshot,
    factory: DebugOverlayDomFactory = defaultDomFactory(),
  ): void {
    const playerPosition = snapshot.playerPosition ?? snapshot.state.player;
    const completionState = snapshot.completionState ?? { cleared: false, perfected: false };
    const saveState = snapshot.saveState ?? EMPTY_SAVE_STATE;

    this.setField(container, "scene-name", snapshot.sceneName ?? "none", factory);
    this.setField(container, "level-id", snapshot.levelId ?? "none", factory);
    this.setField(container, "tick", String(snapshot.tick), factory);
    this.setField(
      container,
      "player-position",
      `${playerPosition.x},${playerPosition.y},${playerPosition.facing}`,
      factory,
    );
    this.setField(
      container,
      "completion-state",
      completionState.cleared ? "completed" : "incomplete",
      factory,
    );
    this.setField(container, "save-state", JSON.stringify(saveState), factory);
    this.setField(container, "debug-surface", serializeDebugSnapshot(snapshot), factory);
  }

  public renderError(
    container: HTMLElement,
    message: string,
    factory: DebugOverlayDomFactory = defaultDomFactory(),
  ): void {
    this.setField(container, "boot-error", message, factory);
  }

  private setField(
    container: HTMLElement,
    testId: string,
    value: string,
    factory: DebugOverlayDomFactory,
  ): void {
    let element = this.elements.get(testId) ?? null;

    if (element === null) {
      element = factory.createElement("div");
      element.dataset["testid"] = testId;
      container.appendChild(element);
      this.elements.set(testId, element);
    }

    element.textContent = value;
  }
}
